import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";

import DashboardLayout from "@/components/DashboardLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminSubjects from "@/pages/admin/AdminSubjects";

import TeacherDashboard from "@/pages/teacher/TeacherDashboard";
import TeacherQuestions from "@/pages/teacher/TeacherQuestions";
import TeacherTests from "@/pages/teacher/TeacherTests";
import TeacherTestBuilder from "@/pages/teacher/TeacherTestBuilder";
import TeacherResults from "@/pages/teacher/TeacherResults";
import TeacherAssignments from "@/pages/teacher/TeacherAssignments";
import TeacherMaterials from "@/pages/teacher/TeacherMaterials";
import TeacherNotify from "@/pages/teacher/TeacherNotify";

import StudentDashboard from "@/pages/student/StudentDashboard";
import StudentTests from "@/pages/student/StudentTests";
import StudentTakeTest from "@/pages/student/StudentTakeTest";
import StudentResults from "@/pages/student/StudentResults";
import StudentAssignments from "@/pages/student/StudentAssignments";
import StudentMaterials from "@/pages/student/StudentMaterials";
import StudentNotifications from "@/pages/student/StudentNotifications";

import "@/App.css";

function Protected({ roles, children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10 font-serif text-ink">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function RoleRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10 font-serif text-ink">Loading…</div>;
  if (!user) return <Landing />;
  if (user.role === "admin") return <Navigate to="/admin" replace />;
  if (user.role === "teacher") return <Navigate to="/teacher" replace />;
  return <Navigate to="/student" replace />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster richColors position="top-right" />
        <Routes>
          <Route path="/" element={<RoleRedirect />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Navigate to="/login" replace />} />

          <Route element={<Protected roles={["admin"]}><DashboardLayout /></Protected>}>
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/users" element={<AdminUsers />} />
            <Route path="/admin/subjects" element={<AdminSubjects />} />
          </Route>

          <Route element={<Protected roles={["admin", "teacher"]}><DashboardLayout /></Protected>}>
            <Route path="/teacher" element={<TeacherDashboard />} />
            <Route path="/teacher/questions" element={<TeacherQuestions />} />
            <Route path="/teacher/tests" element={<TeacherTests />} />
            <Route path="/teacher/tests/new" element={<TeacherTestBuilder />} />
            <Route path="/teacher/results" element={<TeacherResults />} />
            <Route path="/teacher/assignments" element={<TeacherAssignments />} />
            <Route path="/teacher/materials" element={<TeacherMaterials />} />
            <Route path="/teacher/notify" element={<TeacherNotify />} />
          </Route>

          <Route element={<Protected roles={["student"]}><DashboardLayout /></Protected>}>
            <Route path="/student" element={<StudentDashboard />} />
            <Route path="/student/tests" element={<StudentTests />} />
            <Route path="/student/tests/:id" element={<StudentTakeTest />} />
            <Route path="/student/results" element={<StudentResults />} />
            <Route path="/student/assignments" element={<StudentAssignments />} />
            <Route path="/student/materials" element={<StudentMaterials />} />
            <Route path="/student/notifications" element={<StudentNotifications />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
